import { i18n } from '@/locales/locales'

export interface LocalizedVideoOptions {
  fallbackLanguage?: string
}

const DEFAULT_OPTIONS: Required<LocalizedVideoOptions> = {
  fallbackLanguage: 'de',
}

export class LocalizedVideo {
  private videos: Map<string, string> = new Map()
  private options: Required<LocalizedVideoOptions>

  constructor(
    videos: Record<string, string> & { de: string },
    options: LocalizedVideoOptions = {},
  ) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    }

    // Validate that German video is provided
    if (!videos.de) {
      throw new Error('German ("de") video source is required')
    }

    // Add all videos to the map
    Object.entries(videos).forEach(([language, src]) => {
      this.addVideo(language, src)
    })
  }

  /**
   * Add a video for a specific language
   */
  addVideo(language: string, src: string): void {
    this.videos.set(language, src)
  }

  /**
   * Remove a video for a specific language
   */
  removeVideo(language: string): boolean {
    return this.videos.delete(language)
  }

  /**
   * Get video source URL for the current app language
   */
  getCurrentLocaleVideoSrc(): string {
    const currentLanguage = i18n.locale.value
    return this.getVideoSrc(currentLanguage)
  }

  /**
   * Get video source URL for a specific language
   */
  getVideoSrc(language: string): string {
    // Try exact language match first
    let src = this.videos.get(language)

    if (src) {
      return src
    }

    // Try language without country code (e.g., 'en' from 'en-US')
    const baseLanguage = language.split('-')[0]
    src = this.videos.get(baseLanguage)

    if (src) {
      return src
    }

    // Fallback to default language
    if (this.options.fallbackLanguage) {
      src = this.videos.get(this.options.fallbackLanguage)
      if (src) {
        return src
      }
    }

    // Return first available video if no fallback matches
    const firstVideo = Array.from(this.videos.values())[0]
    return firstVideo
  }

  /**
   * Get all available language codes
   */
  getAvailableLanguages(): string[] {
    return Array.from(this.videos.keys())
  }

  /**
   * Check if a video exists for a specific language
   */
  hasVideo(language: string): boolean {
    return this.videos.has(language)
  }

  /**
   * Check if any videos exist
   */
  hasAnyVideo(): boolean {
    return this.videos.size > 0
  }

  /**
   * Get the current app language code
   */
  static getCurrentLanguage(): string {
    return i18n.locale.value
  }
}
